import { Identify } from '@amplitude/analytics-browser'
import * as amplitude from '@amplitude/unified'
import { IAnalyticsEventUserData } from '@parta-io/schemes/dist/modules/analytics/types/eventUserData'
import { IAnalyticsUserData } from '@parta-io/schemes/dist/modules/analytics/types/userData'
import { createGroupsFromProperties } from '@parta-io/schemes/dist/modules/analytics/utils/createGroupsFromProperties'

import { OS_NAME } from './constants'
import { AnalyticsOsNameEnum, IEventGroupData } from './types'

let isInitialized = false

export enum InitAmplitudeEnvEnum {
  PRODUCTION = 'PRODUCTION',
  DEVELOPMENT = 'DEVELOPMENT',
}

const amplitudeConfigVariants: {
  [key in InitAmplitudeEnvEnum]: {
    apiKey: string
  }
} = {
  [InitAmplitudeEnvEnum.PRODUCTION]: {
    apiKey: '39478528e5ff740135b9daae6e7effc2',
  },
  [InitAmplitudeEnvEnum.DEVELOPMENT]: {
    apiKey: '72fc5c9c134629388fc15602b3e551fc',
  },
}

// TODO: We have to imporove frontend configuration
export const initAmplitude = (
  configOption: InitAmplitudeEnvEnum = InitAmplitudeEnvEnum.PRODUCTION,
) => {
  isInitialized = true
  // We don't need to await initialization, because amplitude initializes asynchronously and queues events until it's ready
  // https://github.com/amplitude/Amplitude-TypeScript/blob/main/packages/analytics-core/CHANGELOG.md#bug-fixes-54
  amplitude
    .initAll(amplitudeConfigVariants[configOption].apiKey, {
      analytics: {
        autocapture: {
          attribution: true,
          fileDownloads: true,
          formInteractions: true,
          pageViews: true,
          sessions: true,
          elementInteractions: false,
          frustrationInteractions: false,
          networkTracking: true,
          webVitals: false,
          pageUrlEnrichment: true,
        },
      },
    })
    .catch(() => {
      isInitialized = false
    })
}

interface IAmplitudeUserProperties extends Partial<IAnalyticsUserData> {
  userEmail: string
}

interface IAmplitudeEventProperties extends Partial<IAnalyticsEventUserData> {
  userEmail: string
  companyId: string
  companyName: string
  employeeId: string
  employeeName: string
  employeeRole: string
  employeePermissions: string[]
  windowWidth: number
  windowHeight: number
  hostname: string
  osName: AnalyticsOsNameEnum
}

// Data used for analytics events, but not sent to Amplitude as user properties or event properties
const amplitudeData: {
  amplitudeUserId?: string
  user?: IAmplitudeUserProperties
  event?: IAmplitudeEventProperties
} = {}

export const setUserId = (userId: string) => {
  amplitudeData.amplitudeUserId = userId
  if (isInitialized) {
    amplitude.setUserId(userId)
  }
}

export const setUserInfo = (info: {
  companyId: string
  companyName: string
  employeeId: string
  employeeName: string
  employeeRole: string
  employeePermissions: string[]
  userEmail: string
}) => {
  amplitudeData.user = {
    userEmail: info.userEmail,
  }
  amplitudeData.event = {
    companyId: info.companyId,
    companyName: info.companyName,
    employeeId: info.employeeId,
    employeeName: info.employeeName,
    employeeRole: info.employeeRole,
    employeePermissions: info.employeePermissions,
    userEmail: info.userEmail,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    hostname: window.location.hostname,
    osName: OS_NAME,
  }
  if (isInitialized) {
    const identify = new Identify()
    // TODO: Suggest to remove it (excl: userEmail) because user can be in multiple companies and have different
    // employee roles and permissions in each of them. It is better to send this data as event
    // properties, not user properties. The same applies for different devices and browsers (window size, hostname, etc.)
    identify.set('companyId', info.companyId)
    identify.set('companyName', info.companyName)
    identify.set('employeeId', info.employeeId)
    identify.set('employeeRole', info.employeeRole)
    identify.set('employeePermissions', info.employeePermissions)
    identify.set('userEmail', info.userEmail)
    identify.set('windowWidth', window.innerWidth)
    identify.set('windowHeight', window.innerHeight)
    identify.set('hostname', window.location.hostname)

    amplitude.identify(identify)
  }
}

export const resetUserInfo = () => {
  amplitudeData.amplitudeUserId = undefined
  amplitudeData.user = undefined
  amplitudeData.event = undefined
  if (isInitialized) {
    amplitude.reset()
  }
}

export interface IAmplitudeContext {
  deviceId?: string
  sessionId?: number
  userId?: string
  companyId?: string
  companyName?: string
  employeeId?: string
  employeeRole?: string
  employeePermissions?: string[]
  userEmail?: string
}

// Snapshot of current amplitude context — used by DevWidget for bug reports.
// Returns nothing meaningful before initAmplitude / setUserInfo are called.
export const getAmplitudeContext = (): IAmplitudeContext => {
  const ev = amplitudeData.event
  return {
    deviceId: isInitialized ? amplitude.getDeviceId() : undefined,
    sessionId: isInitialized ? amplitude.getSessionId() : undefined,
    userId: amplitudeData.amplitudeUserId,
    companyId: ev?.companyId,
    companyName: ev?.companyName,
    employeeId: ev?.employeeId,
    employeeRole: ev?.employeeRole,
    employeePermissions: ev?.employeePermissions,
    userEmail: ev?.userEmail ?? amplitudeData.user?.userEmail,
  }
}

export const sendEvent = (
  event: string,
  data: object,
  metadata: { groups: IEventGroupData } = { groups: {} },
) => {
  if (isInitialized) {
    const eventProperties = {
      ...amplitudeData.event,
      ...data,
    }
    amplitude.track(event, eventProperties, {
      groups: {
        ...createGroupsFromProperties(eventProperties),
        ...metadata.groups,
      },
    })
  }
}
